import { TabPanel } from '@mui/lab';
import { Avatar, Divider, List, ListItem, ListItemAvatar, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Image, Protect, Scrollbar } from 'app/components';
import React from 'react';
import { useSelector } from 'react-redux';

const TabPanelInfo = ({ transportSelectedItem }) => {
  const { session } = useSelector((state) => state.session);
  const isIDShown = [config.userRoles.SUPERADMIN, config.userRoles.ADMIN, config.userRoles.ENGINEER].includes(session?.role_id);

  return (
    <TabPanel value='info'>
      <Protect
        permissions={['read', 'activity']}
        renderPermitted={() => (
          <Stack
            direction='row'
            sx={{
              width: '100%',
              height: '100%',
              '.MuiPaper-elevation': {
                width: '100%',
                bgcolor: 'background.default',
                borderRadius: '0',
              },
            }}>
            <Paper elevation={0}>
              <Scrollbar>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    height: '100%',
                    padding: theme.spacing(0),
                    '&:last-of-type': {
                      marginBottom: theme.spacing(2),
                    },
                    '.MuiDivider-root': {
                      position: 'sticky',
                      top: '0',
                      zIndex: '1',
                    },
                  })}>
                  <Stack
                    direction='column'
                    alignItems='center'
                    sx={(theme) => ({
                      padding: theme.spacing(3, 0),
                      bgcolor: 'background.paper',
                      '&:after': {
                        content: '""',
                        height: '1px',
                        width: '100%',
                        position: 'sticky',
                        top: '0',
                      },
                      '.MuiAvatar-root': {
                        width: '56px',
                        height: '56px',
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        svg: {
                          width: '30px',
                          height: '30px',
                        },
                        path: {
                          fill: theme.palette.primary.main,
                        },
                      },
                      '.MuiTypography-root:first-of-type': {
                        marginTop: theme.spacing(1),
                      },
                    })}>
                    <Typography variant='body1'>
                      {transportSelectedItem.name}
                      {isIDShown && ` (ID: ${transportSelectedItem.id})`}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack
                    direction='row'
                    sx={(theme) => ({
                      display: 'grid',
                      gap: 1,
                      width: '560px',
                      margin: 'auto',
                      marginTop: theme.spacing(2),
                      '.MuiPaper-root': {
                        position: 'relative',
                        padding: theme.spacing(2),
                        paddingTop: theme.spacing(6),
                        borderRadius: theme.shape.borderRadius,
                      },
                      '.MuiPaper-label': {
                        position: 'absolute',
                        top: '10px',
                        left: theme.spacing(1),
                        padding: theme.spacing(0, 1),
                        bgcolor: 'background.paper',
                        fontWeight: '500',
                      },
                      '.MuiPaper-row': {
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        '.MuiTypography-root:first-of-type': {
                          color: 'text.secondary',
                        },
                      },
                      '.MuiPaper-root.MuiPaper-tank': {
                        display: 'grid',
                        gap: 3,
                        gridTemplateColumns: `repeat(${transportSelectedItem.tank.length || config.numberOfTank.slice(0, -1).length}, 1fr)`,
                      },
                      '.MuiPaper-row .MuiTypography-root:first-of-type': {
                        gridRow: '1',
                        gridColumn: '1/3',
                      },
                      '.MuiPaper-row .MuiTypography-root:last-of-type': {
                        gridRow: '1',
                        gridColumn: '3/5',
                        'span:nth-of-type(2)': {
                          paddingLeft: theme.spacing(0.5),
                        },
                      },
                      '.MuiPaperProgress-root': {
                        minWidth: '60px',
                        '.MuiPaperProgress-determinate': {
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          width: '100%',
                          height: '100%',
                          minHeight: '90px',
                          maxHeight: '90px',
                          padding: '0 12px',
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          borderRadius: theme.shape.borderRadius,
                          '.MuiTypography-root': {
                            textAlign: 'center',
                            color: theme.palette.text.primary,
                          },
                        },
                        '.MuiPaperProgress-caption': {
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: theme.spacing(1),
                          '.MuiTypography-root': {
                            fontSize: '11px',
                            textAlign: 'center',
                            color: theme.palette.text.primary,
                          },
                        },
                      },
                      '.MuiTypography-caption': {
                        color: 'text.secondary',
                      },
                    })}>
                    <Paper variant='outlined'>
                      <Typography className='MuiPaper-label' variant='body1'>
                        Системная информация
                      </Typography>
                      <Stack className='MuiPaper-row'>
                        <Typography variant='body1'>Регистрационный номер</Typography>
                        <Typography variant='body1'>{transportSelectedItem.number}</Typography>
                      </Stack>
                      <Stack className='MuiPaper-row'>
                        <Typography variant='body1'>Идентифицикационный номер</Typography>
                        <Typography variant='body1'>{transportSelectedItem.vin}</Typography>
                      </Stack>
                      <Stack className='MuiPaper-row'>
                        <Typography variant='body1'>Объем бензобака</Typography>
                        <Typography variant='body1'>{transportSelectedItem.volume}</Typography>
                      </Stack>
                      <Protect
                        permissions='create'
                        renderPermitted={() => (
                          <React.Fragment>
                            <Stack className='MuiPaper-row'>
                              <Typography variant='body1'>Sim0</Typography>
                              {/^7/.test(transportSelectedItem.sim0) && (
                                <Typography variant='body1'>{`+${transportSelectedItem.sim0.substr(0, 1)} (${transportSelectedItem.sim0.substr(1, 3)}) ${transportSelectedItem.sim0.substr(4, 3)}-${transportSelectedItem.sim0.substr(7, 4)}`}</Typography>
                              )}
                              {/^375/.test(transportSelectedItem.sim0) && (
                                <Typography variant='body1'>{`+${transportSelectedItem.sim0.substr(0, 3)} (${transportSelectedItem.sim0.substr(3, 2)}) ${transportSelectedItem.sim0.substr(5, 3)}-${transportSelectedItem.sim0.substr(8, 4)}`}</Typography>
                              )}
                              {/^994/.test(transportSelectedItem.sim0) && (
                                <Typography variant='body1'>{`+${transportSelectedItem.sim0.substr(0, 3)} (${transportSelectedItem.sim0.substr(3, 2)}) ${transportSelectedItem.sim0.substr(5, 3)}-${transportSelectedItem.sim0.substr(8, 4)}`}</Typography>
                              )}
                            </Stack>
                            <Stack className='MuiPaper-row'>
                              <Typography variant='body1'>Sim1</Typography>
                              {/^7/.test(transportSelectedItem.sim1) && (
                                <Typography variant='body1'>{`+${transportSelectedItem.sim1.substr(0, 1)} (${transportSelectedItem.sim1.substr(1, 3)}) ${transportSelectedItem.sim1.substr(4, 3)}-${transportSelectedItem.sim1.substr(7, 4)}`}</Typography>
                              )}
                              {/^375/.test(transportSelectedItem.sim1) && (
                                <Typography variant='body1'>{`+${transportSelectedItem.sim1.substr(0, 3)} (${transportSelectedItem.sim1.substr(3, 2)}) ${transportSelectedItem.sim1.substr(5, 3)}-${transportSelectedItem.sim1.substr(8, 4)}`}</Typography>
                              )}
                              {/^994/.test(transportSelectedItem.sim1) && (
                                <Typography variant='body1'>{`+${transportSelectedItem.sim1.substr(0, 3)} (${transportSelectedItem.sim1.substr(3, 2)}) ${transportSelectedItem.sim1.substr(5, 3)}-${transportSelectedItem.sim1.substr(8, 4)}`}</Typography>
                              )}
                            </Stack>
                          </React.Fragment>
                        )}
                        renderForbidden={() => <React.Fragment />}
                      />
                    </Paper>
                    {['galileosky'].includes(config.transportDeviceTypes[transportSelectedItem.device_type]) && (
                      <Paper variant='outlined'>
                        <Typography className='MuiPaper-label' variant='body1'>
                          Данные за выбранный период
                        </Typography>
                        <Stack className='MuiPaper-row'>
                          <Typography variant='body1'>Пробег</Typography>
                          <Typography variant='body1'>
                            {transportSelectedItem.conditions.length !== 0
                              ? transportSelectedItem.conditions[transportSelectedItem.conditions.length - 1].data.total_mileage !==
                                  config.conditions.total_mileage.default &&
                                transportSelectedItem.conditions[0].data.total_mileage !== config.conditions.total_mileage.default
                                ? transportSelectedItem.conditions[transportSelectedItem.conditions.length - 1].data.total_mileage > 0 &&
                                  `${Math.round((transportSelectedItem.conditions[transportSelectedItem.conditions.length - 1].data.total_mileage - transportSelectedItem.conditions[0].data.total_mileage) / 1000).toLocaleString('ru-RU')} ${config.conditions.total_mileage.unit}`
                                : ''
                              : ''}
                          </Typography>
                        </Stack>
                        <Stack className='MuiPaper-row'>
                          <Typography variant='body1'>Расход топлива</Typography>
                          <Typography variant='body1'>
                            {transportSelectedItem.conditions.length !== 0
                              ? transportSelectedItem.conditions[transportSelectedItem.conditions.length - 1].data
                                  .total_fuel_consumption !== config.conditions.total_fuel_consumption.default &&
                                transportSelectedItem.conditions[0].data.total_fuel_consumption !==
                                  config.conditions.total_fuel_consumption.default
                                ? transportSelectedItem.conditions[transportSelectedItem.conditions.length - 1].data
                                    .total_fuel_consumption > 0 &&
                                  `${((transportSelectedItem.conditions[transportSelectedItem.conditions.length - 1].data.total_fuel_consumption - transportSelectedItem.conditions[0].data.total_fuel_consumption) / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ${config.conditions.total_fuel_consumption.unit}`
                                : ''
                              : ''}
                          </Typography>
                        </Stack>
                      </Paper>
                    )}
                    <Paper variant='outlined'>
                      <Typography className='MuiPaper-label' variant='body1'>
                        Прицеп
                      </Typography>
                      <Stack className='MuiPaper-row'>
                        <Typography variant='body1'>Регистрационный номер</Typography>
                        <Typography variant='body1'>{transportSelectedItem.trailer_number ?? 'Нет данных'}</Typography>
                      </Stack>
                      <Stack className='MuiPaper-row'>
                        <Typography variant='body1'>Идентифицикационный номер</Typography>
                        <Typography variant='body1'>{transportSelectedItem.trailer_vin ?? 'Нет данных'}</Typography>
                      </Stack>
                    </Paper>
                    <Paper variant='outlined' className='MuiPaper-tank'>
                      <Typography className='MuiPaper-label' variant='body1'>
                        Секции
                      </Typography>
                      {(transportSelectedItem.tank.length ? transportSelectedItem.tank : config.numberOfTank.slice(0, -1)).map((_, key) => (
                        <Stack key={key} direction='column' alignItems='center' className='MuiPaperProgress-root'>
                          {transportSelectedItem.tasks[key] ? (
                            <React.Fragment>
                              <div className='MuiPaperProgress-determinate'>
                                <Typography variant='caption'>{transportSelectedItem.tasks[key]?.volume}</Typography>
                              </div>
                              <div className='MuiPaperProgress-caption'>
                                <Typography variant='caption'>{transportSelectedItem.tasks[key]?.product}</Typography>
                              </div>
                            </React.Fragment>
                          ) : (
                            <div className='MuiPaperProgress-determinate' />
                          )}
                        </Stack>
                      ))}
                    </Paper>
                    <Paper
                      variant='outlined'
                      sx={(theme) => ({
                        '.MuiList-root': {
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          padding: '0',
                        },
                        '.MuiListItem-root': {
                          width: '100%',
                          userSelect: 'none',
                          padding: theme.spacing(0),
                          borderRadius: '0',
                          '.MuiListItemAvatar-root': {
                            minWidth: '44px',
                          },
                          '.MuiListItemText-root': {
                            margin: theme.spacing(0),
                          },
                        },
                      })}>
                      <Typography className='MuiPaper-label' variant='body1'>
                        Водители
                      </Typography>
                      <List>
                        {transportSelectedItem.tasks.length !== 0 &&
                          Array.from(new Set(transportSelectedItem.tasks.map((task) => task.driver))).map((driver) => (
                            <ListItem dense key={driver}>
                              <ListItemAvatar>
                                <Avatar>
                                  <Image name='avatar-driver.png' alt='avatar-driver.png' />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText primary={driver} />
                            </ListItem>
                          ))}
                      </List>
                      {transportSelectedItem.tasks.length === 0 && (
                        <Typography variant='caption'>В этом разделе будут отображаться водители текущего задания</Typography>
                      )}
                    </Paper>
                  </Stack>
                </Stack>
              </Scrollbar>
            </Paper>
          </Stack>
        )}
        renderForbidden={() => (
          <React.Fragment>
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={(theme) => ({
                width: '100%',
                height: '100%',
                padding: theme.spacing(2),
              })}>
              <Typography align='center' variant='body1' component='span'>
                Доступ ограничен
              </Typography>
              <Typography align='center' color='text.secondary' variant='caption' component='span'>
                Под вашей учетной записью доступ ограничен
              </Typography>
            </Stack>
          </React.Fragment>
        )}
      />
    </TabPanel>
  );
};
export default TabPanelInfo;
